import { useCallback, useMemo } from 'react';
import {
  OneOfElements,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import {
  IElementProperty,
  ElementPropertyName,
  ElementSelectPropertyCountType,
  IElementSelectProperty,
  IFactoryProperty,
  IElementPropertyOption,
} from '@shared/models/element-property.model';
import {
  getElementProperties,
  isElementPropertySourceCategory,
  getElementAndQuantityProperties,
  PropertyChange,
  isSelectPropertyOptionValue,
  isElementPropertySourceRecipe,
  isElementPropertySourceNone,
  getSelectPropertyOptions,
  isInheritanceSupported,
} from '@shared/helpers/element-property.helpers';
import {
  getSelectPropertyCountChange,
  getSelectPropertyInheritSource,
} from './select-property-inherit.helpers';
import { createPropertySelectOptions } from '@shared/helpers/element-property-factory.helpers';
import {
  getElementById,
  isElement,
  isOneOfPropertyElements,
} from '@shared/helpers/recursive-element.helpers';
import { useUIState } from '@client/store/ui';
import { useResolvedVersion } from './use-resolved-version.hooks';
import amplitudeLog from '@client/amplitude';
import {
  describeExpressionCycle,
  findExpressionCycles,
  formatExpressionVariableName,
} from '@shared/helpers/expression-variables.helpers';
import { createExpression } from '@shared/helpers/expression-factory.helpers';
import { getId } from '@shared/helpers/object.helpers';
import {
  getCategorySourcedPropertiesInTemplateOrder,
  getOptionalProperties,
} from '@shared/helpers/element-category.helpers';
import { getElementCategoryId } from '@shared/helpers/element-category-id.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { propertyChangesToProjectChanges } from '@shared/helpers/pipeline/property-change-to-project-change.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { useIsSelectedOrganizationWhitelisted } from '@client/store/organization/organization.hook';
import {
  AMP_ELEMENT_PROPERTY_ADD,
  AMP_ELEMENT_PROPERTY_DELETE,
  AMP_ELEMENT_PROPERTY_UPDATE,
  AMP_PRODUCT_UPDATE_LIFETIME,
  AMP_PRODUCT_UPDATE_REUSE,
  ELEMENT_PROPERTY_SELECT_INHERIT_COUNT,
} from '@shared/constants';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import {
  getDataTableIdFromProperty,
  getPropertiesByDataTableId,
} from '@shared/helpers/data-tables';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  contentCoverageFactoryOverridesFrom,
  isContentCoverageFactoryShape,
  toContentCoverageFactory,
} from '@shared/templates/categories/common-element-category-properties.helpers';

const PROPERTY_TO_PRODUCT_EVENT: Partial<Record<ElementPropertyName, string>> =
  {
    [ElementPropertyName.Lifetime]: AMP_PRODUCT_UPDATE_LIFETIME,
    [ElementPropertyName.ReusedContent]: AMP_PRODUCT_UPDATE_REUSE,
  };

/**
 * Get element properties.
 * @param element
 * @returns
 */
export const useElementProperties = (
  element: Parameters<typeof getElementProperties>[0],
): IElementProperty[] =>
  useMemo(() => getElementProperties(element), [element]); // Memoize for undefined properties

/** Apply an unsaved expression edit before cycle detection. */
const withModifiedExpression = (
  properties: IElementProperty[],
  modifiedProperty: IElementProperty,
  expression: string | undefined,
): IElementProperty[] => {
  if (!expression) return properties;
  return properties.map((p) =>
    p.name === modifiedProperty.name
      ? ({ ...p, count: createExpression(expression) } as IElementProperty)
      : p,
  );
};

/**
 * Detect whether the property is part of a circular reference loop — persisted
 * or introduced by an unsaved expression edit. Covers edges through system
 * `fallbackCount` expressions too (DEV-3642).
 * @param element
 * @param modifiedProperty The property that is being modified. Pass undefined to turn off circular error checking
 * @param expression The new expression in modifiedProperty
 * @returns Excel-style cycle text, or undefined
 */
export const useCircularError = (
  element: OneOfElements | undefined,
  modifiedProperty: IElementProperty | undefined,
  expression: string | undefined,
): string | undefined => {
  const properties = useMemo(
    () => getElementAndQuantityProperties(element),
    [element],
  );

  return useMemo(() => {
    if (!modifiedProperty) return undefined;
    const checkedProperties = withModifiedExpression(
      properties,
      modifiedProperty,
      expression,
    );
    const key = formatExpressionVariableName(modifiedProperty.name);
    const cycle = findExpressionCycles(checkedProperties).find((c) =>
      c.includes(key),
    );
    return cycle && describeExpressionCycle(cycle);
  }, [expression, modifiedProperty, properties]);
};

/**
 * Update an element property. Supports quantity properties and element properties.
 * Converts {@link PropertyChange} items to {@link ProjectChange} items and delegates
 * to {@link useApplyProjectChanges} so changes are recorded in proposal mode.
 * Surfaces apply-layer validation errors via {@link useApplyProjectChanges}
 * (`showErrorsInSnackbar: true`). Amplitude updates run only after a successful apply.
 */
export const useUpdateProperty = (element: OneOfPropertyElements) => {
  const applyChanges = useApplyProjectChanges({ showErrorsInSnackbar: true });

  return useCallback(
    async (...changes: PropertyChange[]): Promise<void> => {
      const projectChanges = propertyChangesToProjectChanges(element, changes);

      if (projectChanges.length === 0) return;

      const applyResult = await applyChanges(projectChanges);

      if (applyResult.error || applyResult.cancelled) return;

      const change = changes[0];
      const changedPropertyName = change?.name;
      const changedPropertyValue = change?.count;

      const specificEvent =
        changedPropertyName &&
        PROPERTY_TO_PRODUCT_EVENT[
          changedPropertyName as keyof typeof PROPERTY_TO_PRODUCT_EVENT
        ];

      if (specificEvent) {
        amplitudeLog(specificEvent, {
          ElementID: element.id,
          PropertyName: changedPropertyName,
          PropertyValue: changedPropertyValue,
        });
        return;
      }

      amplitudeLog(AMP_ELEMENT_PROPERTY_UPDATE, {
        ElementID: element.id,
        PropertyName: changedPropertyName,
        PropertyValue: changedPropertyValue,
      });
    },
    [applyChanges, element],
  );
};

/**
 * Remove one or more properties from an element.
 * Resolves data-table siblings so removing one table-linked property
 * also removes all properties tied to the same data table.
 * Delegates to {@link useApplyProjectChanges} via {@link ProjectChangeType.ElementPropertyRemove}.
 */
export const useRemoveProperty = (element: OneOfPropertyElements) => {
  const elementId = getId(element);
  const currentProperties = useElementProperties(element);
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    async (
      ...propertyOrIds: ItemOrItemId<IElementProperty>[]
    ): Promise<void> => {
      const ids = propertyOrIds.map(getId);

      const propertiesToRemove = currentProperties.filter((p) =>
        ids.includes(p.id),
      );

      // Include sibling properties connected to the same data table
      const dataTableIds = propertiesToRemove
        .map(getDataTableIdFromProperty)
        .filter(guards.is.defined);

      const tablePropertyIds = getPropertiesByDataTableId(
        currentProperties,
        ...dataTableIds,
      ).map(getId);

      const propertyIds = [...new Set([...ids, ...tablePropertyIds])];

      if (propertiesToRemove.length === 0) {
        throw new Error('Could not find property to remove');
      }

      await applyChanges([
        {
          type: ProjectChangeType.ElementPropertyRemove,
          elementId,
          propertyIds,
        },
      ]);

      amplitudeLog(AMP_ELEMENT_PROPERTY_DELETE, {
        ElementID: elementId,
        PropertyType: propertiesToRemove[0]?.type,
        PropertyName: propertiesToRemove[0]?.displayName,
      });
    },
    [elementId, currentProperties, applyChanges],
  );
};

/**
 * Add a property to an element.
 * Delegates to {@link useApplyProjectChanges} via {@link ProjectChangeType.ElementPropertyAdd}.
 */
export const useAddProperty = (element: OneOfPropertyElements) => {
  const applyChanges = useApplyProjectChanges();
  const { setAddedPropertyId } = useUIState('setAddedPropertyId');

  return useCallback(
    async (property: IFactoryProperty = {}) => {
      const factory: IFactoryProperty = isContentCoverageFactoryShape(property)
        ? toContentCoverageFactory(
            contentCoverageFactoryOverridesFrom(property),
          )
        : {
            inheritFallback: true,
            ...property,
          };

      const applyResult = await applyChanges([
        {
          type: ProjectChangeType.ElementPropertyAdd,
          elementId: element.id,
          factory,
        },
      ]);

      // Cancel can't happen here because we're not asking for confirmation
      if (applyResult.cancelled || applyResult.error) {
        throw new Error(
          applyResult.error instanceof Error
            ? applyResult.error.message
            : String(applyResult.error ?? 'Could not add property'),
        );
      }

      const updatedElement = getElementById(applyResult.project, element.id);

      const newProperty =
        isOneOfPropertyElements(updatedElement) &&
        updatedElement.properties?.at(-1);

      if (!newProperty) {
        throw new Error('Could not add property');
      }

      amplitudeLog(AMP_ELEMENT_PROPERTY_ADD, {
        ElementID: element.id,
        PropertyType: newProperty.type,
        PropertyName: newProperty.displayName,
      });

      setAddedPropertyId(newProperty.id);
      return newProperty;
    },
    [element, setAddedPropertyId, applyChanges],
  );
};

// TODO: Must make ONE updateProperty function for both element and quantity properties (regardless of type)
export const useSetElementSelectPropertyCount = (
  element: OneOfPropertyElements,
): ((
  value: ElementSelectPropertyCountType,
  property: IElementSelectProperty,
) => Promise<void>) => {
  const updateProperty = useUpdateProperty(element);
  const resolvedVersion = useResolvedVersion();

  return useCallback(
    async (value, property) => {
      await updateProperty(
        getSelectPropertyCountChange(property, value, {
          inheritSource: getSelectPropertyInheritSource(
            resolvedVersion,
            element,
          ),
        }),
      );
    },
    [element, resolvedVersion, updateProperty],
  );
};

/**
 * Get all custom and recipe properties (custom properties saved in recipe) from an element
 * @param element
 * @returns
 */
export const useRecipeAndCustomElementProperties = (element: OneOfElements) => {
  const properties = useElementProperties(element);
  return useMemo(
    () =>
      properties.filter(
        (p) =>
          isElementPropertySourceNone(p) || isElementPropertySourceRecipe(p),
      ),
    [properties],
  );
};

/**
 * Category-backed properties for the element sidebar and dialogs, in the same order as the
 * category template (main + optional definitions), then any remaining category-sourced rows.
 *
 * @param element
 * @param excludeProperties Properties that should not be included (eg needed to hide mutual properties in EditProductDialog)
 * @returns
 */
export const useCategoryElementProperties = (
  element: OneOfElements,
  excludeProperties?: string[],
) => {
  return useMemo(() => {
    const base =
      isElement(element) &&
      getElementCategoryId(element) !== ElementCategoryID.None
        ? getCategorySourcedPropertiesInTemplateOrder(element)
        : getElementProperties(element).filter(isElementPropertySourceCategory);

    return base.map((p) => ({
      ...p,
      hidden: p.hidden || (excludeProperties?.includes(p.name) ?? false), // Preserve existing hidden flags and add exclusions
    }));
  }, [element, excludeProperties]);
};

/**
 * Get optional properties from category
 * @param element
 * @returns
 */
export const useOptionalPropertiesFromCategory = (
  element: OneOfPropertyElements,
) => useMemo(() => getOptionalProperties(element), [element]);

/**
 * Get all optional properties that can be added to an element
 * @param element
 * @returns
 */
export const useOptionalProperties = (element: OneOfPropertyElements) => {
  const optionalProperties = useOptionalPropertiesFromCategory(element);
  const existingProperties = useElementProperties(element);
  const isWhitelisted = useIsSelectedOrganizationWhitelisted();

  return useMemo(() => {
    const names = existingProperties.map((p) => p.name);
    return optionalProperties
      .filter((p) => p.name && !names.includes(p.name))
      .filter(isWhitelisted);
  }, [existingProperties, optionalProperties, isWhitelisted]);
};

/**
 * Get options for a select property.
 * Will also add inheritance "auto" option if inheritance is supported
 * @param property
 * @returns
 */
export const useSelectPropertyOptions = (
  property: IElementSelectProperty,
): IElementPropertyOption[] =>
  useMemo(() => {
    const options = getSelectPropertyOptions(property);

    // If inheritance is supported, add the inheritance option to option list
    if (isInheritanceSupported(property)) {
      // Should not happen, but show error to catch this in sentry
      if (
        isSelectPropertyOptionValue(
          property,
          ELEMENT_PROPERTY_SELECT_INHERIT_COUNT,
        )
      ) {
        console.error(
          `Inheritable property "${property.name}" have forbidden option "${ELEMENT_PROPERTY_SELECT_INHERIT_COUNT}, hence inheritance will not be applied for safety"`,
        );
      }
      return [
        ...createPropertySelectOptions([ELEMENT_PROPERTY_SELECT_INHERIT_COUNT]),
        ...options,
      ];
    }
    return options;
  }, [property]);
